import type { QueryClient } from "@tanstack/react-query";
import type { ComponentType } from "react";

import type { AgentService } from "@/shared/domain/agents";
import type {
  BoardRecord,
  BoardService,
  CreateBoardRequest,
} from "@/shared/domain/boards";
import type {
  CollectionRecord,
  CollectionService,
} from "@/shared/domain/collections";
import type { CreateCollectionVariables } from "@/shared/domain/collections/hooks/useCreateCollection";
import type { UpdateCollectionVariables } from "@/shared/domain/collections/hooks/useUpdateCollection";
import type { CustomFieldService } from "@/shared/domain/customFields/CustomFieldService";
import type { EventService } from "@/shared/domain/events/EventService";
import type { FileService } from "@/shared/domain/files/FileService";
import type { LayoutService } from "@/shared/domain/layouts/LayoutService";
import type { MetricsService } from "@/shared/domain/metrics/MetricsService";
import type { QueryTarget } from "@/shared/domain/query";
import type { QueryService } from "@/shared/domain/query/QueryService";
import type { SessionService } from "@/shared/domain/sessions/SessionService";
import type { SkillService } from "@/shared/domain/skills/SkillService";
import type { TopicService } from "@/shared/domain/topics/TopicService";
import type { ViewRecord, ViewService } from "@/shared/domain/views";
import {
  RobotoDomainException,
  RobotoNotFoundException,
} from "@/shared/http/exceptions";
import { Goto } from "@/shared/navigation";
import type { WorkspaceActionPayload } from "@/shared/services/AiAgentThreadPubSubBus/models";
import type { ResourceTableViewingContext } from "@/shared/services/ViewingContextService/ViewingContext";

import type { RequestsApplyTo } from "../requestsApplyTo";

import type { ApprovalMode } from "./approvalModes";

/**
 * Required prefix for every client-declared tool name.
 *
 * Distinguishes client tools from server-side tools at every layer (API,
 * SDK, UI, Bedrock toolConfig). The underscore form is used so the resulting
 * name still matches Bedrock's Converse `toolSpec.name` pattern.
 *
 * Mirrors `CLIENT_TOOL_NAME_PREFIX` in
 * `packages/roboto/src/roboto/ai/core/record.py`.
 */
export const CLIENT_TOOL_NAME_PREFIX = "client_";

/**
 * Pattern enforced on every client tool's `name` field — mirrors the
 * Pydantic validator on `ClientToolSpec.name` in the SDK so a registry
 * misconfiguration trips at registration time rather than at the API.
 */
export const CLIENT_TOOL_NAME_PATTERN = /^client_[a-z][a-z0-9_]*$/;

/**
 * Returns true iff `name` is in the client-tool namespace.
 *
 * Used by the thread UI to decide whether to render a "Client Tool" indicator
 * and by the dispatcher to filter mixed-turn tool_uses (see
 * {@link findPendingClientToolUses}).
 */
export function isClientToolName(name: string): boolean {
  return name.startsWith(CLIENT_TOOL_NAME_PREFIX);
}

/**
 * Strips the client-namespace prefix for display.
 *
 * Server tool names are returned unchanged so the helper is safe to call on
 * any `tool_name`.
 */
export function displayToolName(name: string): string {
  return isClientToolName(name)
    ? name.slice(CLIENT_TOOL_NAME_PREFIX.length)
    : name;
}

/**
 * Specification for a client-side tool that executes in the browser.
 */
export interface ClientToolSpec {
  /** Unique name identifying the tool. Must match {@link CLIENT_TOOL_NAME_PATTERN}. */
  name: string;
  /** Human-readable description of what the tool does. */
  description: string;
  /** JSON Schema describing the tool's input parameters. */
  input_schema: Record<string, unknown>;
}

/**
 * Mutable state shared by the tools of one batch, so a tool can see what the
 * tools before it did.
 *
 * Owned by {@link ClientToolBatchRunner}, which creates one per batch and
 * merges it into the deps of every `execute` — including the approval path,
 * which is handed freshly-read deps. It is deliberately not hung off the
 * hook's deps ref (React state the tools must not write) nor module scope
 * (which would leak a navigation across batches, threads, and tabs).
 */
export interface ClientToolBatchScope {
  /**
   * A tool earlier in this batch called `goto.visualize`.
   *
   * The route has not committed yet — `isOnVisualizerPage()` still answers
   * false, and its closure was captured before the batch started anyway — so
   * this is the only way a later tool can tell that a visualizer is on its
   * way. A tool that reads it must publish into that workspace instead of
   * deep-linking a second one: `Goto.visualize` stashes a single-panel layout
   * and the route loader builds a fresh workspace from it, so a second call
   * either aborts the first navigation or is silently ignored by
   * `shouldRevalidate` and left stranded in sessionStorage.
   */
  navigatedToVisualizer?: boolean;

  /**
   * A tool in this batch waited out the readiness budget and the visualizer
   * never started listening.
   *
   * Latched so the rest of the batch fails fast instead of each re-deriving
   * the same answer at five seconds apiece.
   */
  visualizerUnreachable?: boolean;
}

/**
 * Dependencies that client tools may need from React context.
 *
 * Pure functions can't call hooks, so the hook collects these deps and passes
 * them into the tool executor.
 */
export interface ClientToolDeps {
  goto: Goto;
  orgId?: string;
  eventService?: EventService;
  topicService?: TopicService;
  fileService?: FileService;
  /**
   * Layout read client. Used by `client_visualize_apply_layout` to fetch the
   * named layout before publishing it to the open workspace. Read-only.
   */
  layoutService?: LayoutService;
  /**
   * Agent read/list client. Used by `client_create_agent` in update mode to
   * resolve the caller-named agent to an id in the browser before navigating
   * into the editor. Optional so tools that don't touch agents (and their
   * tests) need not supply it.
   */
  agentService?: AgentService;
  /**
   * Skill read client. Used by `client_create_skill` in update mode to
   * confirm the caller-named skill exists and isn't plainly read-only before
   * navigating. Optional on the same grounds as `agentService`.
   */
  skillService?: SkillService;
  /**
   * Caller's user id, needed to judge who may edit a skill. Optional because
   * it is absent until auth resolves.
   */
  userId?: string;
  collectionService?: CollectionService;
  /**
   * Board client the board tools read a saved board through and write it back
   * through. Reads go to the platform, not the query cache, because the
   * revision a conditional write carries has to be the one the platform holds
   * when the write is sent.
   */
  boardService?: BoardService;
  /** Creates a personal board through the mutation that also updates the cache. */
  createBoard?: (request: CreateBoardRequest) => Promise<BoardRecord>;
  /**
   * The board the request this batch answers applies to, from that
   * request's `misc_context.requests_apply_to`; absent when it named none
   * (`requestsApplyToOf` in `../requestsApplyTo.ts`). `client_create_board`
   * refuses while it is set, unless the call says the user asked for a new
   * board.
   */
  requestsApplyTo?: RequestsApplyTo;
  /**
   * The services and cache the board tools hand to the board authoring service
   * (`shared/domain/boards/boardAuthoringService.ts`) for a dry run, which only
   * reads through them. The source stages (`entity.query`, `metric.aggregate`,
   * `metric.values`, `topic.data`) fetch through these services and
   * `topicService`, under the board page's own cache keys in `queryClient`, so
   * the board page's first render reuses the dry run's results.
   * `customFieldService` lists the custom fields that type an
   * `entity.query` source's custom-field columns.
   */
  customFieldService?: CustomFieldService;
  metricsService?: MetricsService;
  queryService?: QueryService;
  sessionService?: SessionService;
  queryClient?: QueryClient;
  isOnVisualizerPage?: () => boolean;
  /**
   * The router's current path, relative to the app's base path. A tool that
   * navigates to a route reads this to skip the navigation when the user is
   * already there.
   */
  currentRoutePath?: () => string;
  publishWorkspaceAction?: (payload: WorkspaceActionPayload) => boolean;
  /**
   * State shared with the other tools of this batch. Supplied by the runner
   * for every execution; optional so a tool called directly (or from a test
   * that has no batch) still type-checks.
   */
  batchScope?: ClientToolBatchScope;
  onEventCreated?: () => void;
  /**
   * Collection writes, as the mutation hooks' `mutateAsync`.
   *
   * Deliberately NOT `collectionService.createCollection` /
   * `.updateCollection`: those write without touching the cache, so every
   * caller had to remember a second "now refresh" step, and forgetting it left
   * the UI showing stale membership with nothing to indicate why. Taking the
   * hook's write instead makes the invalidation unskippable — an agent editing
   * a collection and a user clicking through the UI now run the same code.
   *
   * `collectionService` remains above for reads (`getCollectionById`), which
   * carry no such hazard.
   */
  createCollection?: (
    variables: CreateCollectionVariables,
  ) => Promise<CollectionRecord>;
  updateCollection?: (
    variables: UpdateCollectionVariables,
  ) => Promise<CollectionRecord>;
  /**
   * View read client, for `client_apply_view` to resolve the View it opens.
   * Writes are server tools (create_view, update_view, delete_view).
   */
  viewService?: ViewService;
  /**
   * The resource table the user is looking at, read at call time rather than
   * captured: a tool runs after an approval, and the table may have moved in
   * between. `undefined` when no list is on screen.
   */
  resourceTableOnScreen?: () => ResourceTableViewingContext | undefined;
  /**
   * Apply a View to the table on screen, in place. Returns `false` when no
   * table is listening, in which case the tool navigates to the View's table
   * instead.
   */
  applyViewToOpenTable?: (view: ViewRecord) => boolean;
  /**
   * Make a View the caller's default for its table (`null` clears it), the
   * same per-user preference the picker's "Set as default" writes. Resolves
   * once the picker will reflect it.
   */
  setDefaultView?: (
    target: QueryTarget,
    viewId: string | null,
  ) => Promise<void>;
}

/**
 * Execution result returned by every client tool's `execute` function.
 */
export interface ClientToolExecutionResult {
  response: Record<string, unknown>;
  success: boolean;
}

/** A tool's refusal of a call, or undefined when the call may go on to its
 *  approval and its execution. */
export type RefusalBeforeApproval = (
  params: Record<string, unknown>,
  deps?: ClientToolDeps,
) => ClientToolExecutionResult | undefined;

/**
 * Props passed to a tool's custom confirmation content component.
 *
 * `T` is the tool's input type — the same type its `execute` receives, which
 * keeps a preview from being written against a shape the tool does not accept.
 * It stops short of pinning the two together: props are contravariant, so a
 * preview may be declared over any supertype of `T`, and that hatch is in use
 * (`CreateEventsConfirmation` declares every field optional to avoid an import
 * cycle). A field added to `T` therefore need not show up in the preview. What
 * does keep a proposal read the same way on both sides is sharing the code that
 * reads it: each collection tool and its preview run the same coercion from
 * `collectionToolInput.ts`, so what is approvable is what is sendable.
 *
 * `params` is a `T` only for tools that declare
 * {@link ClientToolConfig.validateInput}: {@link confirmationContentFor}
 * re-applies that guard before rendering. Tools without one
 * (`client_create_events`, `client_create_skill`, `client_create_agent`) hand
 * their preview the model's raw JSON, so those components must read defensively.
 */
export interface ConfirmationContentProps<T = Record<string, unknown>> {
  /** The parameters the LLM proposed for this tool invocation. */
  params: T;
}

/**
 * Complete client tool including spec, execution, approval configuration,
 * and optional UI for the confirmation modal.
 *
 * This is the registry-facing interface — tools register instances of this
 * type (or use {@link createClientTool} to build one from a typed config).
 */
export interface ClientTool {
  spec: ClientToolSpec;

  /** Default approval mode for this tool (used when the user has no stored preference). */
  defaultApprovalMode: ApprovalMode;

  /**
   * Which approval modes the user is allowed to select for this tool.
   *
   * For destructive tools like delete_dataset, this should be
   * `[ApprovalMode.REQUIRE_APPROVAL]` to prevent users from disabling the
   * confirmation prompt. The default approval mode MUST be included in this list.
   */
  allowableApprovalModes: ApprovalMode[];

  /** Human-readable label for the settings modal (e.g., "Navigate", "Create Events"). */
  displayName: string;

  /** Short description shown in the settings modal. */
  displayDescription: string;

  /** Execute the tool with the given params. Called after approval (if required). */
  execute: (
    params: Record<string, unknown>,
    deps?: ClientToolDeps,
  ) => Promise<ClientToolExecutionResult>;

  /** {@link ClientToolConfig.refusalBeforeApproval}, for the batch runner to
   *  apply before it asks for approval. `execute` applies it too. */
  refusalBeforeApproval?: RefusalBeforeApproval;

  /**
   * Runtime half of {@link ClientToolConfig.validateInput}, kept on the
   * registry-facing tool so the approval UI can apply the same gate `execute`
   * does before rendering a preview.
   *
   * Erased to `boolean` here: the registry holds tools of many input types, so
   * the narrowing is only expressible at the config.
   */
  validateInput?: (raw: unknown) => boolean;

  /**
   * Optional React component rendered inside the confirmation modal when
   * this tool requires approval. Receives the proposed tool parameters and
   * renders a read-only preview of what the tool will do.
   *
   * If not provided, a generic confirmation dialog is shown with the raw
   * parameters displayed as formatted JSON. Use {@link confirmationContentFor}
   * to pick which of the two a given set of params should get.
   */
  ConfirmationContent?: ComponentType<ConfirmationContentProps>;
}

/**
 * The preview component to render for `params`, or `undefined` for none.
 *
 * Returns the tool's `ConfirmationContent` only when the tool has one *and*
 * the tool's `validateInput`, if it declares one, accepts the params: a
 * component declared over the tool's validated input must not be handed params
 * the tool would have rejected. Callers fall back to the raw-JSON preview,
 * which is the right thing to show for a malformed proposal anyway.
 */
export function confirmationContentFor(
  tool: ClientTool,
  params: Record<string, unknown>,
): ComponentType<ConfirmationContentProps> | undefined {
  if (!tool.ConfirmationContent) {
    return undefined;
  }
  if (tool.validateInput && !tool.validateInput(params)) {
    return undefined;
  }
  return tool.ConfirmationContent;
}

// ---------------------------------------------------------------------------
// Result builders
// ---------------------------------------------------------------------------

/**
 * What kind of failure a tool hit, as a value the model can branch on.
 *
 * A single flattened error string forces the model to guess from prose whether
 * to fix its arguments or try again, and it guesses wrong in the expensive
 * direction: "Failed to create collection" reads as retryable.
 */
export enum ToolErrorKind {
  /** The request itself is wrong. Retrying it unchanged fails the same way. */
  VALIDATION = "validation",
  /** The named resource does not exist (or is not visible to this caller). */
  NOT_FOUND = "not_found",
  /** The request never completed. Whether it took effect is unknown. */
  TRANSPORT = "transport",
  /** The resource changed, or was still being changed, after the request read it. Nothing was written; read it again and retry. */
  CONFLICT = "conflict",
  /** The request could not finish this time, and nothing was written. The same request can succeed when sent again. */
  UNAVAILABLE = "unavailable",
}

/**
 * What the model is told to do when a request that could not finish (an
 * `UNAVAILABLE` error) is sent once more and fails the same way. The meaning
 * of `UNAVAILABLE` ends with it, and so does every error of that kind, so the
 * error and the meaning the model reads beside it never name different steps.
 */
export const WHEN_THE_REQUEST_FAILS_AGAIN =
  "If the second attempt fails the same way, take any step a failure says " +
  "to take if it recurs; otherwise tell the user what could not run, " +
  "naming it as the user sees it rather than pasting a failure's message, " +
  "and that asking again later may work.";

/**
 * What each {@link ToolErrorKind} means, in the words the model reads.
 *
 * Emitted next to the value because nothing else defines it: `error_kind`
 * appears in no tool description and in no schema, so a bare `"transport"`
 * would be a token the model has to guess the semantics of on the one turn
 * where guessing wrong means repeating a write.
 */
const ERROR_KIND_MEANING: Record<ToolErrorKind, string> = {
  [ToolErrorKind.VALIDATION]:
    "The request itself is wrong. Retrying it unchanged fails the same way.",
  [ToolErrorKind.NOT_FOUND]:
    "The named resource does not exist, or is not visible to you.",
  [ToolErrorKind.TRANSPORT]:
    "The request never completed. Whether it took effect is unknown.",
  [ToolErrorKind.CONFLICT]:
    "The resource changed, or was still being changed, after the request read it. Nothing was written; read it again and retry.",
  [ToolErrorKind.UNAVAILABLE]:
    "The request could not finish this time, and nothing was written. The same request may succeed: send it once more. " +
    WHEN_THE_REQUEST_FAILS_AGAIN,
};

/**
 * Build an error result for a client tool execution.
 *
 * `details` is merged into the response alongside `error` — use it for
 * structured, machine-readable context (e.g. per-item failure reasons) that
 * lets the LLM adjust its request and retry. The `error` key is reserved:
 * a `details.error` entry is ignored so it can never clobber the message.
 *
 * Classifying with `kind` also emits `error_kind_meaning`. `kind` is optional
 * only so that pre-existing tools keep their current response shape; new
 * failure paths should classify.
 */
export function toolError(
  message: string,
  details: Record<string, unknown> = {},
  kind?: ToolErrorKind,
): ClientToolExecutionResult {
  return {
    response: {
      ...details,
      ...(kind !== undefined && {
        error_kind: kind,
        error_kind_meaning: ERROR_KIND_MEANING[kind],
      }),
      error: message,
    },
    success: false,
  };
}

/**
 * Classify a thrown error into the kind of failure the model should act on.
 *
 * A 4xx is the model's problem to fix; anything else (a network failure, a
 * 5xx, a timeout) leaves the outcome unknown, which is the distinction the
 * model needs before deciding whether calling again is safe.
 */
export function classifyToolFailure(error: unknown): ToolErrorKind {
  if (error instanceof RobotoNotFoundException) {
    return ToolErrorKind.NOT_FOUND;
  }
  if (error instanceof RobotoDomainException && error.isClientError()) {
    return ToolErrorKind.VALIDATION;
  }
  return ToolErrorKind.TRANSPORT;
}

/** Message text for a thrown error, for inclusion in a tool result. */
export function describeToolFailure(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Build a success result for a client tool execution.
 */
export function toolSuccess(
  action: string,
  extra: Record<string, unknown> = {},
): ClientToolExecutionResult {
  return { response: { success: true, action, ...extra }, success: true };
}

// ---------------------------------------------------------------------------
// Typed tool factory
// ---------------------------------------------------------------------------

/**
 * Configuration accepted by {@link createClientTool}.
 *
 * @typeParam T - The validated input type for the tool.
 */
export interface ClientToolConfig<T> {
  /** Unique name identifying the tool. */
  name: string;
  /** Human-readable description of what the tool does. */
  description: string;
  /** JSON Schema describing the tool's input parameters. */
  input_schema: Record<string, unknown>;
  /**
   * Optional type-guard that validates raw input before `execute` is called.
   *
   * When provided, the wrapper calls `validateInput(params)` first. If it
   * returns `false`, the tool returns a generic validation-error result
   * without ever calling `execute`.
   *
   * When omitted, all input is forwarded to `execute` unconditionally
   * (cast to `T`).
   */
  validateInput?: (raw: unknown) => raw is T;
  /**
   * Tool implementation that receives the validated input.
   *
   * If `validateInput` is provided and passes, `input` is guaranteed to
   * satisfy `T`. If `validateInput` is omitted, `input` is a blind cast
   * from the raw params — the tool author is responsible for defensive
   * checks inside `execute`.
   */
  execute: (
    input: T,
    deps?: ClientToolDeps,
  ) => ClientToolExecutionResult | Promise<ClientToolExecutionResult>;
  /**
   * Optional refusal decided from the call's input and deps alone, before
   * the user is asked to approve the call. When it returns a result, the
   * batch runner records that result without asking for approval, and the
   * tool's `execute` returns it without running.
   */
  refusalBeforeApproval?: RefusalBeforeApproval;
  /** Default approval mode for this tool (used when the user has no stored preference). */
  defaultApprovalMode: ApprovalMode;
  /**
   * Which approval modes the user is allowed to select for this tool.
   * The default approval mode MUST be included in this list.
   */
  allowableApprovalModes: ApprovalMode[];
  /** Human-readable label for the settings modal (e.g., "Navigate", "Create Events"). */
  displayName: string;
  /** Short description shown in the settings modal. */
  displayDescription: string;
  /**
   * Optional React component for rich approval preview.
   *
   * Typed over the same `T` as `execute`, so the preview and the write cannot
   * be written against different readings of the proposal.
   */
  ConfirmationContent?: ComponentType<ConfirmationContentProps<T>>;
}

/**
 * Factory that builds a {@link ClientTool} from a typed configuration.
 *
 * The returned tool's `execute` wraps the caller-supplied `execute` with the
 * optional `refusalBeforeApproval` and `validateInput` guards, in that order.
 * A refusal is returned as the tool gave it; a failed validation returns a
 * generic error result. Either way the caller-supplied `execute` never runs.
 *
 * @example
 * ```ts
 * interface SearchInput { query: string }
 *
 * const searchTool = createClientTool<SearchInput>({
 *   name: "search",
 *   description: "Search datasets",
 *   input_schema: { type: "object", properties: { query: { type: "string" } }, required: ["query"] },
 *   execute(input) {
 *     return { response: { results: [] }, success: true };
 *   },
 *   defaultApprovalMode: ApprovalMode.FOLLOW_AUTO,
 *   allowableApprovalModes: [ApprovalMode.REQUIRE_APPROVAL, ApprovalMode.FOLLOW_AUTO, ApprovalMode.AUTO_EXECUTE],
 *   displayName: "Search",
 *   displayDescription: "Searches datasets.",
 * });
 * ```
 */
export function createClientTool<T>(config: ClientToolConfig<T>): ClientTool {
  if (!CLIENT_TOOL_NAME_PATTERN.test(config.name)) {
    // Fail fast at registration so a misnamed tool can never reach the API,
    // where the Pydantic validator on ClientToolSpec.name would reject the
    // whole request and break the thread session.
    throw new Error(
      `Client tool name "${config.name}" must match ${CLIENT_TOOL_NAME_PATTERN}` +
        ` (start with "${CLIENT_TOOL_NAME_PREFIX}", then lowercase letters,` +
        ` digits, and underscores).`,
    );
  }
  if (!config.allowableApprovalModes.includes(config.defaultApprovalMode)) {
    // Also fail fast: `resolveApprovalMode` returns `defaultApprovalMode`
    // unvalidated when the user has no stored preference, which is the default
    // state for every user. An unlisted default is therefore the effective mode
    // for almost everyone while the settings modal never offers it and the
    // allowable list never constrains it -- a tool pinned to REQUIRE_APPROVAL
    // could auto-execute in practice.
    throw new Error(
      `Client tool "${config.name}" has defaultApprovalMode ` +
        `"${config.defaultApprovalMode}", which is not in its allowableApprovalModes ` +
        `[${config.allowableApprovalModes.join(", ")}].`,
    );
  }
  return {
    spec: {
      name: config.name,
      description: config.description,
      input_schema: config.input_schema,
    },
    defaultApprovalMode: config.defaultApprovalMode,
    allowableApprovalModes: config.allowableApprovalModes,
    displayName: config.displayName,
    displayDescription: config.displayDescription,
    validateInput: config.validateInput,
    // The registry is heterogeneous, so `T` has to be erased here. For a tool
    // that declares `validateInput` the cast is checked at the seam:
    // `confirmationContentFor` re-applies the guard before rendering, the same
    // gate `execute` applies below. For a tool without one it is unchecked and
    // the component receives whatever the model emitted.
    ConfirmationContent: config.ConfirmationContent as
      ComponentType<ConfirmationContentProps> | undefined,
    refusalBeforeApproval: config.refusalBeforeApproval,
    async execute(params, deps) {
      const refusal = config.refusalBeforeApproval?.(params, deps);
      if (refusal !== undefined) {
        return refusal;
      }
      if (config.validateInput && !config.validateInput(params)) {
        return toolError(
          `Invalid input for ${config.name}.`,
          {},
          ToolErrorKind.VALIDATION,
        );
      }
      return config.execute(params as T, deps);
    },
  };
}
